import axios from 'axios';
import config from 'config';

/**
 * kilo_mcp lives on its own domain, so it gets its own axios instance, separate from the kilo
 * API's `client` in `api-client.ts`. This is the single place that origin is configured — the
 * URL helpers below read it back off `defaults.baseURL` rather than re-reading config, so there
 * is one source of truth.
 *
 * No `withCredentials` and no CF Access headers: the cross-domain OAuth handshake deliberately
 * shares no cookie with the staff app, which is the whole reason the handoff token exists. The
 * staff app's own MCP endpoints (`mcp/authorizable-profiles/`, `mcp-connections/`) live on the
 * kilo API and go through `client`, so nothing issues requests through this instance today.
 */
export const mcpClient = axios.create({
  baseURL: config.MCP_API_SERVER,
});

const mcpBaseUrl = (): string => mcpClient.defaults.baseURL ?? '';

// What the owner pastes into Claude's custom-connector setup.
export const MCP_CONNECTION_URL = `${mcpBaseUrl()}/mcp/`;

const mcpServerOrigin = (): string | null => {
  try {
    return new URL(mcpBaseUrl()).origin;
  } catch {
    return null;
  }
};

/**
 * True when `url` is absolute and points at the configured kilo_mcp origin.
 *
 * The picker receives its return URL as a query param, so everything the browser is sent to
 * must be checked against the trusted origin first — an unchecked `next` would turn the picker
 * into an open redirect.
 */
export const isTrustedMCPUrl = (url: string | null): boolean => {
  const trusted = mcpServerOrigin();
  if (!url || !trusted) return false;
  try {
    return new URL(url).origin === trusted;
  } catch {
    return false;
  }
};

/** The `state` the OAuth client sent, read back off an already-trusted authorize URL. */
export const readOAuthState = (trustedNextUrl: string): string | null => {
  try {
    return new URL(trustedNextUrl).searchParams.get('state');
  } catch {
    return null;
  }
};

/**
 * Where Cancel sends the browser: kilo_mcp resolves the handoff to the `redirect_uri` it froze
 * when the flow started and returns OAuth's `access_denied` to the client. Built from the
 * instance's own base URL rather than from `next`, so a crafted link cannot choose the
 * destination.
 */
export const buildMCPDenyUrl = (handoff: string, state: string | null): string => {
  const params = new URLSearchParams({ handoff });
  if (state) params.set('state', state);
  return `${mcpBaseUrl()}/oauth/authorize/deny/?${params.toString()}`;
};
